import {
  buildAttachmentAccept,
  inferAttachmentKind,
  normalizeAttachmentPolicy,
  validateAttachmentFile,
} from '@/constants/attachment-policy';
import type {
  HelpAgentAttachmentPolicy,
  HelpAgentAttachmentRejection,
  HelpAgentDraftAttachment,
} from '@/types/attachments';
import { createId } from '@/utils/ids';

export interface HelpAgentAttachmentStoreResult {
  added: HelpAgentDraftAttachment[];
  rejected: HelpAgentAttachmentRejection[];
}

export class HelpAgentAttachmentStore {
  private attachments: HelpAgentDraftAttachment[] = [];
  private policy: HelpAgentAttachmentPolicy = normalizeAttachmentPolicy(null);

  get snapshot(): HelpAgentDraftAttachment[] {
    return [...this.attachments];
  }

  get accept(): string {
    return buildAttachmentAccept(this.policy);
  }

  setPolicy(policy: Partial<HelpAgentAttachmentPolicy> | null | undefined): void {
    this.policy = normalizeAttachmentPolicy(policy);
  }

  addFiles(files: Iterable<File>): HelpAgentAttachmentStoreResult {
    const added: HelpAgentDraftAttachment[] = [];
    const rejected: HelpAgentAttachmentRejection[] = [];
    let currentCount = this.attachments.length;

    for (const file of files) {
      const reason = validateAttachmentFile(file, currentCount, this.policy);
      if (reason) {
        rejected.push({
          fileName: file.name || 'Unnamed file',
          reason,
        });
        continue;
      }

      const kind = inferAttachmentKind(file);
      const previewUrl = kind === 'image' ? URL.createObjectURL(file) : null;
      const attachment: HelpAgentDraftAttachment = {
        id: createId('attachment'),
        file,
        name: file.name || 'Unnamed file',
        size: file.size,
        mimeType: file.type || 'application/octet-stream',
        kind,
        previewUrl,
      };

      this.attachments = [...this.attachments, attachment];
      added.push(attachment);
      currentCount += 1;
    }

    return { added, rejected };
  }

  remove(id: string): void {
    const attachment = this.attachments.find((item) => item.id === id);
    if (attachment?.previewUrl) {
      URL.revokeObjectURL(attachment.previewUrl);
    }

    this.attachments = this.attachments.filter((item) => item.id !== id);
  }

  clear(): void {
    for (const attachment of this.attachments) {
      if (attachment.previewUrl) {
        URL.revokeObjectURL(attachment.previewUrl);
      }
    }

    this.attachments = [];
  }

  destroy(): void {
    this.clear();
  }
}
